import { computed, ref, type ComputedRef, type Ref } from 'vue'

// An in-flight run clears when its first output broadcast lands ("one
// broadcast = one complete turn"); this is the backstop so a run that dies
// silently — no output, no error — doesn't pulse "working…" forever.
const AGENT_RUN_TIMEOUT_MS = 5 * 60_000

export interface AgentRunIndicator {
  agentRunning: Ref<boolean>
  liveStatus: ComputedRef<string | null>
  startRunning(): void
  stopRunning(): void
  pauseRunningIndicator(): void
  setProgress(threadId: string, text: string): void
}

// The local "is a run in flight, and what's it doing" state machine for
// whichever thread is active on the agent store. Extracted (#835) so its
// per-thread caching invariant gets its own testable surface: a
// switch-away-and-back must show a still-running session's own last-known
// text, not a generic reset (#831).
export function useAgentRunIndicator(
  activeThreadId: Ref<string | null>,
  fetchRunning: (threadId: string) => Promise<boolean>,
): AgentRunIndicator {
  // Drives the "agent is working…" cue: true from a relayed message
  // until its output lands. The POST only enqueues, so `sending` (cleared the
  // instant it returns) can't stand in for "the run is still going".
  const agentRunning = ref(false)
  // Live progress text shown in the pending bubble, keyed by thread_id so
  // switching between two concurrently-running sessions shows each one's
  // own last-known status instead of a value carried over from whichever
  // thread last updated it (#831). Set to "Starting…" immediately on send,
  // updated by each progress broadcast, cleared on the final broadcast.
  const liveStatusByThread = ref<Record<string, string | null>>({})
  const liveStatus = computed(() =>
    activeThreadId.value ? (liveStatusByThread.value[activeThreadId.value] ?? null) : null,
  )
  let runTimer: ReturnType<typeof setTimeout> | null = null

  // A run genuinely finished for the active thread: drop its cached status
  // text along with the running flag so a stale bubble can't linger.
  function stopRunning() {
    pauseRunningIndicator()
    if (activeThreadId.value) liveStatusByThread.value[activeThreadId.value] = null
  }

  // Switching views: assume not-running until the async status check
  // resolves, WITHOUT discarding the thread's last-known status text — if
  // it's still running in the background, re-opening it should show where
  // it left off, not a generic reset (#831).
  function pauseRunningIndicator() {
    agentRunning.value = false
    if (runTimer) {
      clearTimeout(runTimer)
      runTimer = null
    }
  }

  function startRunning() {
    agentRunning.value = true
    const threadId = activeThreadId.value
    // Only seed the generic placeholder when nothing is cached yet — a
    // thread resumed after a mere view-switch keeps showing its own
    // last-known progress text instead of resetting to "Starting…".
    if (threadId && liveStatusByThread.value[threadId] == null) {
      liveStatusByThread.value[threadId] = 'Starting…'
    }
    if (runTimer) clearTimeout(runTimer)
    runTimer = setTimeout(() => void pollRunningState(), AGENT_RUN_TIMEOUT_MS)
  }

  // Timer callback: re-check server truth before clearing the "Working…" cue.
  // A long-running claude task outlives AGENT_RUN_TIMEOUT_MS; polling keeps
  // the indicator accurate instead of blindly dropping it.
  async function pollRunningState(): Promise<void> {
    const threadId = activeThreadId.value
    if (!threadId || !agentRunning.value) return
    const running = await fetchRunning(threadId)
    if (!agentRunning.value) return // cleared by final broadcast while we awaited
    if (running) {
      runTimer = setTimeout(() => void pollRunningState(), AGENT_RUN_TIMEOUT_MS)
    } else {
      stopRunning()
    }
  }

  // Record a progress broadcast's text for the given thread — callers only
  // forward broadcasts already known to match the active thread.
  function setProgress(threadId: string, text: string) {
    liveStatusByThread.value[threadId] = text
  }

  return { agentRunning, liveStatus, startRunning, stopRunning, pauseRunningIndicator, setProgress }
}
